import { intranetChatLocationE7, intranetChatMapUrl } from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  GoogleMapFrame,
  GoogleMapPicker,
  canPlacePinOnMap,
} from '@/products/intranet/components/chat-location-map';

export function ChatLocationDialog({
  open,
  initialBody,
  pending,
  onClose,
  onSend,
}: {
  open: boolean;
  initialBody: string;
  pending: boolean;
  onClose: () => void;
  onSend: (input: { body: string; latitudeE7: number; longitudeE7: number }) => void;
}) {
  const copy = useVoiceCopy();
  const [mode, setMode] = useState<'choose' | 'map' | 'preview'>('choose');
  const [pin, setPin] = useState<{ latitude: number; longitude: number } | null>(null);
  const [text, setText] = useState(initialBody);
  const [gpsError, setGpsError] = useState<string | null>(null);

  function requestGps() {
    if (!navigator.geolocation) {
      setGpsError(copy('intranet.location.gps-unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPin({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setGpsError(null);
        setMode('preview');
      },
      () => setGpsError(copy('intranet.location.gps-unavailable')),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
    );
  }

  function send() {
    if (!pin) {
      return;
    }
    onSend({
      body: text.trim(),
      latitudeE7: intranetChatLocationE7(pin.latitude),
      longitudeE7: intranetChatLocationE7(pin.longitude),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Standort</DialogTitle>
        </DialogHeader>
        {mode === 'choose' ? (
          <div className="flex flex-col gap-2">
            <Button type="button" onClick={requestGps}>
              Meinen Standort
            </Button>
            <Button type="button" variant="outline" onClick={() => setMode('map')}>
              Punkt auf der Karte
            </Button>
            {gpsError ? <p className="text-sm text-muted-foreground">{gpsError}</p> : null}
          </div>
        ) : null}
        {mode === 'map' ? (
          <div className="space-y-3">
            <GoogleMapPicker
              latitude={pin?.latitude ?? null}
              longitude={pin?.longitude ?? null}
              onPick={(latitude, longitude) => setPin({ latitude, longitude })}
            />
            {pin ? (
              <Textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                aria-label="Text zum Standort"
                rows={2}
              />
            ) : canPlacePinOnMap() ? (
              <p className="text-sm text-muted-foreground">{copy('intranet.location.tap-map')}</p>
            ) : null}
          </div>
        ) : null}
        {mode === 'preview' && pin ? (
          <div className="space-y-3">
            <GoogleMapFrame latitude={pin.latitude} longitude={pin.longitude} />
            <a
              className="text-sm underline"
              target="_blank"
              rel="noopener noreferrer"
              href={intranetChatMapUrl(
                intranetChatLocationE7(pin.latitude),
                intranetChatLocationE7(pin.longitude),
              )}
            >
              In Karten öffnen
            </a>
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              aria-label="Text zum Standort"
              rows={2}
            />
          </div>
        ) : null}
        {mode !== 'choose' ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMode('choose')}>
              Zurück
            </Button>
            <Button type="button" disabled={!pin || pending} onClick={send}>
              Senden
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
